/**
 * Wholesale Add All Varieties Feature
 * Adds "Add 1 of each" buttons to product groups in wholesale pages
 */

// ============================================
// FIX: Create missing v1 variables for v2 product/quickbuy page compatibility
// The Easy Wholesale app v2 on product/quickbuy pages is missing variables that exist on v1:
// - nm_wh_collection (empty object)
// - nm_easywholesale_show (true)
// This polyfill adds them to prevent the app from crashing.
// ============================================
(function fixWholesaleV2Compatibility() {
    const isProductPage = window.location.pathname.includes('/products/');
    const isQuickOrderPage = window.location.pathname.includes('quick-order');
    const needsPolyfill = isProductPage || isQuickOrderPage;

    if (needsPolyfill) {
        // Set missing v1 variables immediately
        if (typeof window.nm_wh_collection === 'undefined') {
            window.nm_wh_collection = {};
        }
        if (typeof window.nm_easywholesale_show === 'undefined') {
            window.nm_easywholesale_show = true;
        }

        // Also set on DOMContentLoaded in case they get overwritten
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                if (typeof window.nm_wh_collection === 'undefined') {
                    window.nm_wh_collection = {};
                }
                if (typeof window.nm_easywholesale_show === 'undefined') {
                    window.nm_easywholesale_show = true;
                }
            });
        }
    }
})();
// ============================================
// END FIX
// ============================================


class WholesaleAddAll {
    constructor() {
        this.enableQtyQuickPresets = false;
        this.resizeRaf = null;
        this.cartQtyByVariantId = new Map();
        this.cartQtyRefreshInFlight = null;
        this.cartQtyRefreshTimer = null;
        this.cartSyncBound = false;
        this.handleResize = () => {
            if (this.resizeRaf) {
                cancelAnimationFrame(this.resizeRaf);
            }
            this.resizeRaf = requestAnimationFrame(() => {
                this.updateResponsiveRowModes();
            });
        };
        this.init();
    }

    init() {
        // Wait for DOM and wholesale components to load
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                this.setupButtons();
                this.rebuildWholesaleRows();
                this.injectMassyPriceContainers();
                this.applyNameAccessibilityTitles();
                this.enhanceQuantityInteractions();
                this.setupStickyFooter();      // Product pages: make sticky visible
                this.setupAllStickyPrices();   // ALL pages: update sticky prices with Massy discounts
                this.updateResponsiveRowModes();
                this.bindCartQuantitySync();
                this.refreshCartQuantities();
            });
        } else {
            this.setupButtons();
            this.rebuildWholesaleRows();
            this.injectMassyPriceContainers();
            this.applyNameAccessibilityTitles();
            this.enhanceQuantityInteractions();
            this.setupStickyFooter();          // Product pages: make sticky visible
            this.setupAllStickyPrices();       // ALL pages: update sticky prices with Massy discounts
            this.updateResponsiveRowModes();
            this.bindCartQuantitySync();
            this.refreshCartQuantities();
        }

        // Also setup when wholesale content dynamically loads
        this.observeWholesaleChanges();
        window.addEventListener('resize', this.handleResize, { passive: true });
    }

    /**
     * Setup Massy price updates for sticky footer on ALL pages
     * This is page-type agnostic - if a sticky exists, update its prices
     */
    setupAllStickyPrices() {
        this.applyStickyTierStyling();
        const sticky = document.querySelector('.nm-wholesale-main-fix');
        if (sticky) {
            this.setupStickyPriceUpdates(sticky);
        }
    }

    applyStickyTierStyling() {
        const sticky = document.querySelector('.nm-wholesale-main-fix');
        if (!sticky) return;

        const tier = (window.customerTier || '').toLowerCase();
        if (!tier) return;

        ['gold', 'silver', 'bronze', 'starter'].forEach((t) => {
            sticky.classList.remove(`nm-wholesale-main-fix--${t}`);
        });

        sticky.classList.add(`nm-wholesale-main-fix--${tier}`);
    }

    setupButtons() {
        // Check if we're on a product page first - if so, only create one button
        const isProductPage = window.location.pathname.includes('/products/');

        if (isProductPage && document.querySelector('.wholesale-add-all-btn')) {
            return; // Button already exists on product page
        }

        // Define selectors to try
        const selectors = [
            '.nm-easywholesale-product',
            '.nm-wholesale-product',
            '.wholesale-product',
            '[class*="wholesale"][class*="product"]'
        ];

        let productGroups = [];

        // Try each selector until we find elements
        for (const selector of selectors) {
            const elements = document.querySelectorAll(selector);
            if (elements.length > 0) {
                productGroups = elements;
                break;
            }
        }

        // If no direct matches, look for broader wholesale containers
        if (productGroups.length === 0) {
            const containers = document.querySelectorAll('[class*="wholesale"]');
            if (containers.length > 0) {
                productGroups = containers;
            }
        }

        // For product pages, just process the first group and exit
        if (isProductPage && productGroups.length > 0) {
            this.addButtonToGroup(productGroups[0]);
            return;
        }

        // For wholesale pages, process all groups
        productGroups.forEach((group) => {
            // Skip if button already exists
            if (group.querySelector('.wholesale-add-all-btn')) {
                return;
            }

            this.addButtonToGroup(group);
        });
    }

    addButtonToGroup(group) {
        // Skip if button already exists anywhere in this group
        if (group.querySelector('.wholesale-add-all-btn')) return;

        // Check if we're on a product page vs wholesale page
        const isProductPage = window.location.pathname.includes('/products/') &&
            !group.classList.contains('nm-easywholesale-container') &&
            !group.classList.contains('nm-wholesale-container');

        if (isProductPage) {
            this.addButtonToProductPage(group);
        } else {
            this.addButtonToWholesalePage(group);
        }
    }

    addButtonToProductPage(group) {
        // Check if button already exists on the entire page (not just this group)
        if (document.querySelector('.wholesale-add-all-btn')) {
            return;
        }

        // Look for wholesale table header first (preferred location)
        const wholesaleHeader = document.querySelector('.nm-easywholesale-header');
        if (wholesaleHeader) {
            this.addButtonToWholesaleHeader(wholesaleHeader);
            return;
        }

        // Fallback to original ADD TO CART container logic
        const addToCartContainer = document.querySelector('.product-form__cart') ||
            document.querySelector('[class*="add-to-cart"]') ||
            document.querySelector('#AddToCart')?.parentElement ||
            document.querySelector('button[name="add"]')?.parentElement ||
            document.querySelector('.nm-wholesale-table')?.querySelector('.nm-wholesale-bottom');

        if (!addToCartContainer) {
            console.log('No add to cart container found on product page');
            return;
        }

        const button = this.createButton();

        // Style the container to hold both buttons - responsive gap
        const isMobile = window.innerWidth <= 749;
        addToCartContainer.style.display = 'flex';
        addToCartContainer.style.gap = isMobile ? '3px' : '10px';
        addToCartContainer.style.alignItems = 'stretch';
        addToCartContainer.style.flexWrap = 'nowrap';
        addToCartContainer.style.justifyContent = isMobile ? 'flex-start' : 'space-between';
        addToCartContainer.style.width = '100%';

        // Apply mobile-specific styling if needed
        if (isMobile) {
            setTimeout(() => {
                // Apply styles directly to enforce side-by-side layout
                addToCartContainer.style.cssText += `
                    display: flex !important;
                    flex-direction: row !important;
                    flex-wrap: nowrap !important;
                    gap: 3px !important;
                    align-items: stretch !important;
                    justify-content: flex-start !important;
                    width: 100% !important;
                `;

                // Style the wholesale button
                button.style.cssText += `
                    flex: 1 1 60% !important;
                    max-width: 60% !important;
                    margin: 0 !important;
                    font-size: 1.4rem !important;
                    padding: 0.4rem 0.3rem !important;
                    white-space: normal !important;
                    overflow: visible !important;
                    text-overflow: clip !important;
                    line-height: 1.2 !important;
                    transform: none !important;
                    text-align: center !important;
                    max-height: none !important;
                `;

                // Style the ADD TO CART button
                const addToCartBtn = addToCartContainer.querySelector('button[name="add"], #AddToCart');
                if (addToCartBtn) {
                    addToCartBtn.style.cssText += `
                        flex: 1 1 35% !important;
                        max-width: 35% !important;
                        font-size: 0.9rem !important;
                        padding: 0.5rem 0.3rem !important;
                    `;
                }
            }, 100);
        }

        // Add our button to the right side (end of container)
        addToCartContainer.appendChild(button);

        this.attachButtonEvents(button, document.body);
    }

    addButtonToWholesaleHeader(headerContainer) {
        const button = this.createButton();

        // Create a container for the button in the header
        const buttonContainer = document.createElement('div');
        buttonContainer.className = 'wholesale-header-button-container';
        buttonContainer.style.cssText = `
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 10px 0;
            margin-top: 5px;
            width: 100%;
            box-sizing: border-box;
        `;

        // Style the button for header placement
        button.style.cssText += `
            min-width: 200px;
            max-width: 280px;
            width: 50%;
            padding: 0.8rem 1.2rem;
            font-size: 1.3rem;
            font-weight: 700;
            border-radius: 6px;
        `;

        buttonContainer.appendChild(button);
        headerContainer.appendChild(buttonContainer);

        this.attachButtonEvents(button, document.body);
    }

    addButtonToWholesalePage(group) {
        const button = this.createButton();
        const isMobile = window.innerWidth <= 749;

        if (isMobile) {
            // On mobile: add button under the title area
            const titleContainer = group.querySelector('.nm-wholesale-title-foto') ||
                group.querySelector('.nm-wholesale-header') ||
                group.querySelector('[class*="wholesale-header"]') ||
                group.querySelector('[class*="title"]');

            if (!titleContainer) {
                console.log('No title container found for mobile wholesale button');
                return;
            }

            // Create a container for the mobile button under title
            const mobileButtonContainer = document.createElement('div');
            mobileButtonContainer.className = 'wholesale-mobile-title-button-container';
            mobileButtonContainer.style.cssText = `
                width: 100% !important;
                padding: 8px 15px !important;
                display: flex !important;
                justify-content: center !important;
                margin: 5px 0 2px 0 !important;
                background: linear-gradient(145deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.02));
                border-radius: 8px;
                border: 1px solid rgba(255, 255, 255, 0.1);
            `;

            button.style.cssText += `
                width: 100% !important;
                max-width: 300px !important;
                font-size: 1.2rem !important;
                padding: 0.75rem 1rem !important;
            `;

            mobileButtonContainer.appendChild(button);

            // Insert AFTER the title container, not inside it
            titleContainer.parentNode.insertBefore(mobileButtonContainer, titleContainer.nextSibling);
        } else {
            // On desktop: keep inline with title
            let titleElement = group.querySelector('.nm-easywholesale-linktitle') ||
                group.querySelector('.nm-wholesale-header-title a') ||
                group.querySelector('.nm-wholesale-header-title') ||
                group.querySelector('.nm-wholesale-header') ||
                group.querySelector('h3 a') ||
                group.querySelector('h2 a') ||
                group.querySelector('h1 a') ||
                group.querySelector('[class*="title"]') ||
                group.querySelector('[class*="header"]');

            if (!titleElement) {
                console.log('No title element found in wholesale group', group);
                return;
            }

            let titleContainer = titleElement.closest('.nm-wholesale-header') ||
                titleElement.closest('.nm-easywholesale-header') ||
                titleElement.closest('[class*="header"]') ||
                titleElement.parentElement;

            if (!titleContainer) {
                console.log('No title container found');
                return;
            }

            // Make the title container a flex container and add button
            titleContainer.style.display = 'flex';
            titleContainer.style.justifyContent = 'space-between';
            titleContainer.style.alignItems = 'center';
            titleContainer.style.flexWrap = 'wrap';
            titleContainer.appendChild(button);
        }

        this.attachButtonEvents(button, group);
    }

    createButton() {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'wholesale-add-all-btn';
        button.title = 'Add the full line to cart';
        button.innerHTML = `
            <span class="btn-text">Add the full line to cart</span>
            <span class="btn-icon">⚡</span>
        `;
        return button;
    }

    attachButtonEvents(button, group) {
        button.addEventListener('click', (e) => {
            e.preventDefault();
            this.addOneOfEach(group, button);
        });
    }

    async addOneOfEach(group, button) {
        // Show processing state immediately
        button.classList.add('processing');
        const originalText = button.querySelector('.btn-text').textContent;
        button.querySelector('.btn-text').textContent = 'Adding to cart...';

        // Find all item rows in this group
        const rawItems = Array.from(group.querySelectorAll('.nm-wholesale-table tr, [class*="wholesale"] tr, [class*="wholesale"] .row, [class*="wholesale-item"]'));

        // Filter for visible items only (to avoid mobile/desktop duplication)
        const items = rawItems.filter(item => {
            const rect = item.getBoundingClientRect();
            return rect.width > 0 && rect.height > 0;
        });

        let addedCount = 0;
        const itemsToAdd = [];
        const seenVariantIds = new Set();
        let outOfStockVarieties = [];

        console.log(`Checking ${items.length} visible items in group`);

        // First pass: extract all unique visible variant IDs and check stock
        for (const item of items) {
            // Check if item is sold out/disabled
            const isSoldOut = item.classList.contains('disabled') ||
                item.classList.contains('sold-out') ||
                item.querySelector('.sold-out, .disabled');

            // Extract variant ID
            let variantId = item.dataset.variantId ||
                item.dataset.id ||
                item.getAttribute('data-variant-id') ||
                item.getAttribute('data-id');

            if (!variantId) {
                const inputWithId = item.querySelector('[data-variant-id], [data-id], input[name*="id"]');
                if (inputWithId) {
                    variantId = inputWithId.dataset.variantId || inputWithId.dataset.id || inputWithId.name.match(/\[(\d+)\]/)?.[1];
                }
            }

            if (!variantId) {
                const plusBtn = item.querySelector('.nm-easywholesale-qty-increase, [class*="increase"]');
                if (plusBtn) {
                    variantId = plusBtn.dataset.id || plusBtn.dataset.variantId || plusBtn.id.match(/(\d+)$/)?.[1];
                }
            }

            if (variantId) {
                const cleanId = variantId.toString().replace(/^nm-/, '').replace(/\D/g, '');
                if (cleanId && !isNaN(cleanId) && !seenVariantIds.has(cleanId)) {
                    seenVariantIds.add(cleanId);

                    if (isSoldOut) {
                        const varietyName = item.querySelector('.nm-easywholesale-name, [class*="name"]')?.textContent?.trim() || `ID: ${cleanId}`;
                        outOfStockVarieties.push(varietyName);
                    } else {
                        itemsToAdd.push({
                            id: parseInt(cleanId),
                            quantity: 1
                        });
                        addedCount++;
                    }
                }
            }
        }

        // If any of the VISIBLE variants are out of stock, abort everything
        if (outOfStockVarieties.length > 0) {
            console.log('Aborting: Out of stock varieties found:', outOfStockVarieties);
            this.showToast('Some items out of stock.', 'warning');
            this.resetButton(button, originalText);
            return;
        }

        if (itemsToAdd.length > 0) {
            try {
                // Add items directly to Shopify cart
                const response = await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        items: itemsToAdd
                    })
                });

                if (response.ok) {
                    const data = await response.json();
                    console.log('Items successfully added to cart');

                    // Trigger cart drawer update
                    if (window.theme && window.theme.cart) {
                        window.theme.cart.updateCartDrawer();
                    }

                    // Refresh the page immediately to update wholesale UI
                    window.location.reload();
                } else {
                    const errorData = await response.json();
                    console.error('Shopify API Error:', errorData);
                    const errorMessage = errorData.description || errorData.message || 'Failed to add items to cart';
                    this.showToast(errorMessage, 'error');
                    this.resetButton(button, originalText);
                }
            } catch (error) {
                console.error('Fetch Error:', error);
                this.showToast('Error connecting to cart', 'error');
                this.resetButton(button, originalText);
                return;
            }
        } else {
            // Update UI
            if (addedCount === 0) {
                this.showToast('No available varieties found', 'warning');
                this.resetButton(button, originalText);
            }
        }

        // Trigger global recalculation
        this.triggerWholesaleUpdate();
    }

    resetButton(button, originalText) {
        if (button) {
            button.classList.remove('processing');
            if (originalText && button.querySelector('.btn-text')) {
                button.querySelector('.btn-text').textContent = originalText;
            }
        }
    }

    triggerWholesaleUpdate() {
        // Try to trigger any wholesale app recalculation methods
        setTimeout(() => {
            // Common wholesale app update triggers
            window.dispatchEvent(new Event('wholesale:update'));
            document.dispatchEvent(new Event('cart:update'));

            // Trigger any existing wholesale update functions
            if (window.updateWholesaleTotal) window.updateWholesaleTotal();
            if (window.refreshWholesaleCart) window.refreshWholesaleCart();
            if (window.nm_wholesale_update) window.nm_wholesale_update();
        }, 100);
    }

    showToast(message, type = 'success') {
        console.log(`Showing toast: [${type}] ${message}`);
        // Create simple toast notification
        const toast = document.createElement('div');
        toast.className = `wholesale-toast wholesale-toast-${type}`;
        toast.textContent = message;

        document.body.appendChild(toast);

        // Animate in
        setTimeout(() => toast.classList.add('show'), 50);

        // Remove after delay
        setTimeout(() => {
            toast.classList.remove('show');
            setTimeout(() => {
                if (toast.parentNode) document.body.removeChild(toast);
            }, 500);
        }, 4000);
    }

    /**
     * Preserve full SKU name text for accessibility/hover when rows are clamped
     */
    applyNameAccessibilityTitles(root = document) {
        const nameNodes = root.querySelectorAll('.nm-easywholesale-name');
        nameNodes.forEach((node) => {
            const fullName = (node.textContent || '').replace(/\s+/g, ' ').trim();
            if (!fullName) return;
            node.setAttribute('title', fullName);
            node.setAttribute('aria-label', fullName);
        });
    }

    getRowDisplayName(row) {
        const nameNode = row.querySelector('.nm-easywholesale-name');
        if (!nameNode) return '';
        return (nameNode.textContent || '').replace(/\s+/g, ' ').trim();
    }

    findRowMediaNode(row) {
        const mediaSelectors = [
            '.nm-wholesale-title-foto',
            '.nm-easywholesale-media',
            '.product__media-wrapper',
            '.product__media',
            '.nm-wholesale-product-image',
            '.nm-wholesale-title'
        ];

        for (const selector of mediaSelectors) {
            const node = row.querySelector(selector);
            if (node) {
                return node.closest('a, .nm-easywholesale-media, .product__media-wrapper, .product__media, .nm-wholesale-title-foto, .nm-wholesale-product-image, .nm-wholesale-title') || node;
            }
        }

        const imageNode = row.querySelector('img');
        if (!imageNode) return null;
        const imageWrap = imageNode.closest('a, .nm-easywholesale-media, .product__media-wrapper, .product__media, .nm-wholesale-title-foto, .nm-wholesale-product-image, .nm-wholesale-title');
        return imageWrap || imageNode.closest('a') || imageNode.parentElement || imageNode;
    }

    getMainSourceNode(row) {
        const wrapper = row.querySelector('.nm-easywholesale-name-and-price-only') ||
            row.querySelector('.nm-easywholesale-name-and-price');
        if (wrapper) return wrapper;

        const nameNode = row.querySelector('.nm-easywholesale-name');
        if (!nameNode) return null;
        return nameNode.parentElement || nameNode;
    }

    /**
     * Rebuild each wholesale row into a deterministic shell:
     * [media] [name/meta] [rail]
     */
    rebuildWholesaleRows(root = document) {
        const rows = root.querySelectorAll('.nm-easywholesale-item');
        rows.forEach((row) => {
            const isTableRow = row.tagName.toLowerCase() === 'tr';
            const nameNode = row.querySelector('.nm-easywholesale-name');
            const priceNode = row.querySelector('.nm-easywholesale-price');
            const qtyNode = row.querySelector('.nm-easywholesale-qty-container');

            if (!nameNode || !priceNode || !qtyNode) return;

            if (isTableRow) {
                this.normalizeTableRow(row, priceNode, qtyNode);
                return;
            }

            const existingRowShell = row.querySelector(':scope > .gc-wholesale-row-shell');
            if (
                row.dataset.gcRowStable === '1' &&
                existingRowShell &&
                existingRowShell.contains(priceNode) &&
                existingRowShell.contains(qtyNode)
            ) {
                return;
            }

            const mediaNode = this.findRowMediaNode(row);
            const mainSource = this.getMainSourceNode(row);
            const sourceChildren = Array.from(row.children);

            const shell = document.createElement('div');
            shell.className = 'gc-wholesale-row-shell';

            const mediaCell = document.createElement('div');
            const mainCell = document.createElement('div');
            const railCell = document.createElement('div');
            mediaCell.className = 'gc-row-media';
            mainCell.className = 'gc-row-main';
            railCell.className = 'gc-row-rail gc-wholesale-rail';

            if (mediaNode && row.contains(mediaNode)) {
                mediaCell.appendChild(mediaNode);
            } else {
                const spacer = document.createElement('div');
                spacer.className = 'gc-row-media-spacer';
                spacer.setAttribute('aria-hidden', 'true');
                mediaCell.appendChild(spacer);
            }

            const shouldMoveToMain = (node) => {
                if (node === mediaNode || mediaNode?.contains(node) || node?.contains?.(mediaNode)) return false;
                if (node === priceNode || priceNode?.contains(node) || node?.contains?.(priceNode)) return false;
                if (node === qtyNode || qtyNode?.contains(node) || node?.contains?.(qtyNode)) return false;
                return true;
            };

            const mainNodes = new Set();
            if (mainSource && row.contains(mainSource)) {
                mainNodes.add(mainSource);
            }

            sourceChildren.forEach((node) => {
                if (!node.classList?.contains('gc-wholesale-row-shell') && shouldMoveToMain(node)) {
                    mainNodes.add(node);
                }
            });

            mainNodes.forEach((node) => {
                if (node !== row && !mainCell.contains(node)) {
                    mainCell.appendChild(node);
                }
            });

            if (!railCell.contains(priceNode)) {
                railCell.appendChild(priceNode);
            }
            if (!railCell.contains(qtyNode)) {
                railCell.appendChild(qtyNode);
            }

            if (existingRowShell && existingRowShell.parentElement === row) {
                existingRowShell.remove();
            }

            shell.append(mediaCell, mainCell, railCell);
            row.appendChild(shell);
            row.dataset.gcRowStable = '1';
            this.updateResponsiveModeForRow(row);
        });
    }

    normalizeTableRow(row, priceNode, qtyNode) {
        const rowContainer = row.querySelector('td') || row;
        let mainSourceNode = this.getMainSourceNode(row);
        if (mainSourceNode === row || mainSourceNode === rowContainer) {
            mainSourceNode = row.querySelector('.nm-easywholesale-name-and-price-only') ||
                row.querySelector('.nm-easywholesale-name-and-price') ||
                row.querySelector('.nm-easywholesale-name');
        }
        if (!rowContainer || !mainSourceNode) return;

        const existingRowShell = rowContainer.querySelector(':scope > .gc-wholesale-row-shell');
        if (
            row.dataset.gcRowStable === '1' &&
            existingRowShell &&
            existingRowShell.contains(priceNode) &&
            existingRowShell.contains(qtyNode)
        ) {
            this.updateResponsiveModeForRow(row);
            return;
        }

        const shell = document.createElement('div');
        shell.className = 'gc-wholesale-row-shell';

        const mediaCell = document.createElement('div');
        mediaCell.className = 'gc-row-media';

        const mainCell = document.createElement('div');
        mainCell.className = 'gc-row-main';

        const railCell = document.createElement('div');
        railCell.className = 'gc-row-rail gc-wholesale-rail';

        const mediaNode = this.findRowMediaNode(row);
        if (mediaNode) {
            const mediaWrap = mediaNode.closest('a, .nm-easywholesale-media, .product__media-wrapper, .product__media, .nm-wholesale-title-foto, .nm-wholesale-product-image, .nm-wholesale-title');
            const mediaSource = mediaWrap || mediaNode;
            if (!mediaCell.contains(mediaSource)) {
                mediaCell.appendChild(mediaSource);
            }
        } else {
            const spacer = document.createElement('div');
            spacer.className = 'gc-row-media-spacer';
            mediaCell.appendChild(spacer);
        }

        const shouldMoveToMain = (node) => {
            if (!node || node === shell || node === existingRowShell) return false;
            if (node === mediaNode || mediaNode?.contains(node) || node?.contains?.(mediaNode)) return false;
            if (node === priceNode || priceNode?.contains(node) || node?.contains?.(priceNode)) return false;
            if (node === qtyNode || qtyNode?.contains(node) || node?.contains?.(qtyNode)) return false;
            return true;
        };

        const mainNodes = new Set();
        if (mainSourceNode && shouldMoveToMain(mainSourceNode)) {
            mainNodes.add(mainSourceNode);
        }

        Array.from(rowContainer.childNodes).forEach((node) => {
            if (!node.classList?.contains('gc-wholesale-row-shell') && shouldMoveToMain(node)) {
                mainNodes.add(node);
            }
        });

        mainNodes.forEach((node) => {
            if (!node || !node.parentNode) return;
            mainCell.appendChild(node);
        });

        if (!railCell.contains(priceNode)) {
            railCell.appendChild(priceNode);
        }
        if (!railCell.contains(qtyNode)) {
            railCell.appendChild(qtyNode);
        }

        if (existingRowShell && existingRowShell.parentElement === rowContainer) {
            existingRowShell.remove();
        }

        shell.append(mediaCell, mainCell, railCell);
        rowContainer.appendChild(shell);

        row.classList.add('gc-wholesale-table-row');
        row.dataset.gcRowStable = '1';
        this.updateResponsiveModeForRow(row);
    }

    enhanceQuantityInteractions(root = document) {
        const rows = root.querySelectorAll('.nm-easywholesale-item');
        rows.forEach((row) => this.enhanceRowQuantityControls(row));
    }

    updateResponsiveRowModes(root = document) {
        const rows = root.querySelectorAll('.nm-easywholesale-item');
        rows.forEach((row) => this.updateResponsiveModeForRow(row));
    }

    updateResponsiveModeForRow(row) {
        if (!row) return;

        const shell = row.querySelector(':scope > .gc-wholesale-row-shell') ||
            row.querySelector(':scope > td > .gc-wholesale-row-shell') ||
            row.querySelector('.gc-wholesale-row-shell');
        const rail = row.querySelector('.gc-row-rail, .gc-wholesale-rail');
        const main = row.querySelector('.gc-row-main');
        if (!shell || !rail || !main) return;

        const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
        const shellWidth = shell.getBoundingClientRect().width;
        const railWidth = rail.getBoundingClientRect().width;
        const compactThreshold = railWidth + 245;
        const shouldCornerMode = shellWidth <= 640;

        // Keep desktop split view in the regular aligned layout.
        // Compact fallback is only for mobile-size viewports where clipping risk is high.
        const shouldCompact = viewportWidth <= 768 && shellWidth <= compactThreshold;

        row.classList.toggle('gc-row-compact', shouldCompact);
        row.classList.toggle('gc-row-corner-mode', shouldCornerMode);
        this.ensureRowCornerFillers(row);
        this.updateRowCornerFillers(row);
    }

    extractRowVariantId(row) {
        if (!row) return null;
        const qtyInput = row.querySelector('.nm-easywholesale-qty, .nm-easywholesale-qty-input, .quantity__input');
        if (!qtyInput) return null;

        const name = qtyInput.getAttribute('name') || '';
        const nameMatch = name.match(/updates\[(\d+)\]/);
        if (nameMatch) return nameMatch[1];

        const dataId = qtyInput.dataset.id ||
            row.querySelector('.nm-easywholesale-qty-increase')?.dataset.id ||
            row.querySelector('.nm-easywholesale-qty-decrease')?.dataset.id ||
            '';
        const dataIdMatch = String(dataId).match(/(\d{6,})$/);
        if (dataIdMatch) return dataIdMatch[1];

        return null;
    }

    async refreshCartQuantities() {
        if (this.cartQtyRefreshInFlight) {
            return this.cartQtyRefreshInFlight;
        }

        this.cartQtyRefreshInFlight = (async () => {
            try {
                const response = await fetch('/cart.js', {
                    method: 'GET',
                    credentials: 'same-origin',
                    cache: 'no-store',
                    headers: {
                        Accept: 'application/json'
                    }
                });
                if (!response.ok) return;

                const cart = await response.json();
                const nextMap = new Map();
                (cart.items || []).forEach((item) => {
                    const variantId = String(item.variant_id || item.id || '').trim();
                    if (!variantId) return;
                    const qty = Math.max(0, parseInt(item.quantity, 10) || 0);
                    nextMap.set(variantId, (nextMap.get(variantId) || 0) + qty);
                });

                this.cartQtyByVariantId = nextMap;
                this.updateAllRowCornerFillers();
            } catch (error) {
                console.warn('[WholesaleAddAll] Failed to refresh cart quantities for corner chips', error);
            } finally {
                this.cartQtyRefreshInFlight = null;
            }
        })();

        return this.cartQtyRefreshInFlight;
    }

    scheduleCartQtyRefresh(delay = 220) {
        if (this.cartQtyRefreshTimer) {
            clearTimeout(this.cartQtyRefreshTimer);
        }
        this.cartQtyRefreshTimer = setTimeout(() => {
            this.refreshCartQuantities();
        }, delay);
    }

    updateAllRowCornerFillers(root = document) {
        const rows = root.querySelectorAll('.nm-easywholesale-item');
        rows.forEach((row) => this.updateRowCornerFillers(row));
    }

    bindCartQuantitySync() {
        if (this.cartSyncBound) return;
        this.cartSyncBound = true;

        document.addEventListener('click', (event) => {
            if (event.target.closest('.nm-wholesale-btn-add-to-cart, button[name="add"], .wholesale-add-all-btn')) {
                this.scheduleCartQtyRefresh(320);
            }
        });

        window.addEventListener('pageshow', () => this.scheduleCartQtyRefresh(0));
    }

    normalizeRegionTag(rawTag = '') {
        const key = String(rawTag || '')
            .replace(/[^A-Za-z]/g, '')
            .toUpperCase();
        if (!key) return '';
        const map = {
            ON: 'ON',
            ONTARIO: 'ON',
            FED: 'FED',
            FEDERAL: 'FED',
            AB: 'AB',
            ALBERTA: 'AB',
            MB: 'MB',
            MANITOBA: 'MB',
            QC: 'QC',
            QUEBEC: 'QC'
        };
        return map[key] || '';
    }

    getActivePageRegionTag() {
        const activeRegionBtn = document.querySelector('.region-button.active');
        if (activeRegionBtn) {
            const normalized = this.normalizeRegionTag(activeRegionBtn.textContent || '');
            if (normalized) return normalized;
        }

        const path = (window.location.pathname || '').toLowerCase();
        if (path.includes('ontario')) return 'ON';
        if (path.includes('federal')) return 'FED';
        if (path.includes('alberta')) return 'AB';
        if (path.includes('manitoba')) return 'MB';
        if (path.includes('quebec')) return 'QC';
        return '';
    }

    extractRowRegionTag(nameText = '') {
        if (!nameText) return this.getActivePageRegionTag();
        const match = nameText.match(/\[([A-Za-z ]{2,12})\]\s*$/);
        const normalized = this.normalizeRegionTag(match ? match[1] : '');
        return normalized || this.getActivePageRegionTag();
    }

    ensureRowCornerFillers(row) {
        if (!row) return;
        const shell = row.querySelector(':scope > .gc-wholesale-row-shell') ||
            row.querySelector(':scope > td > .gc-wholesale-row-shell') ||
            row.querySelector('.gc-wholesale-row-shell');
        if (!shell) return;

        let fillerLayer = shell.querySelector('.gc-row-corner-fillers');
        if (!fillerLayer) {
            fillerLayer = document.createElement('div');
            fillerLayer.className = 'gc-row-corner-fillers';
            fillerLayer.setAttribute('aria-hidden', 'true');

            const topRight = document.createElement('span');
            topRight.className = 'gc-row-corner-top';
            topRight.textContent = '0 in cart';

            const bottomLeft = document.createElement('span');
            bottomLeft.className = 'gc-row-corner-bottom';
            bottomLeft.hidden = true;

            fillerLayer.append(topRight, bottomLeft);
            shell.appendChild(fillerLayer);
        }

        const qtyInput = row.querySelector('.nm-easywholesale-qty, .nm-easywholesale-qty-input, .quantity__input');
        if (qtyInput && !qtyInput.dataset.gcCornerFillBound) {
            const updateCornerFillers = () => {
                this.updateRowCornerFillers(row);
                this.scheduleCartQtyRefresh(220);
            };
            qtyInput.addEventListener('input', updateCornerFillers);
            qtyInput.addEventListener('change', updateCornerFillers);
            qtyInput.addEventListener('blur', updateCornerFillers);
            qtyInput.dataset.gcCornerFillBound = '1';
        }

        const scheduleFromControls = () => {
            this.scheduleCartQtyRefresh(220);
            window.requestAnimationFrame(() => this.updateRowCornerFillers(row));
        };
        const increaseBtn = row.querySelector('.nm-easywholesale-qty-increase');
        if (increaseBtn && !increaseBtn.dataset.gcCornerFillBound) {
            increaseBtn.addEventListener('click', scheduleFromControls);
            increaseBtn.addEventListener('touchend', scheduleFromControls, { passive: true });
            increaseBtn.dataset.gcCornerFillBound = '1';
        }
        const decreaseBtn = row.querySelector('.nm-easywholesale-qty-decrease');
        if (decreaseBtn && !decreaseBtn.dataset.gcCornerFillBound) {
            decreaseBtn.addEventListener('click', scheduleFromControls);
            decreaseBtn.addEventListener('touchend', scheduleFromControls, { passive: true });
            decreaseBtn.dataset.gcCornerFillBound = '1';
        }
    }

    updateRowCornerFillers(row) {
        if (!row) return;
        const shell = row.querySelector(':scope > .gc-wholesale-row-shell') ||
            row.querySelector(':scope > td > .gc-wholesale-row-shell') ||
            row.querySelector('.gc-wholesale-row-shell');
        if (!shell) return;

        const topRight = shell.querySelector('.gc-row-corner-top');
        const bottomLeft = shell.querySelector('.gc-row-corner-bottom');
        if (!topRight || !bottomLeft) return;

        const variantId = this.extractRowVariantId(row);
        const cartQty = variantId ? (this.cartQtyByVariantId.get(String(variantId)) || 0) : 0;
        topRight.textContent = cartQty === 1 ? '1 in cart' : `${cartQty} in cart`;
        shell.classList.toggle('gc-has-items', cartQty > 0);

        const rowName = this.getRowDisplayName(row) || row.querySelector('.nm-easywholesale-name')?.textContent?.trim() || '';
        const regionTag = this.extractRowRegionTag(rowName);
        if (regionTag) {
            bottomLeft.textContent = regionTag;
            bottomLeft.dataset.region = regionTag;
            bottomLeft.hidden = false;
        } else {
            bottomLeft.textContent = '';
            delete bottomLeft.dataset.region;
            bottomLeft.hidden = true;
        }
    }

    enhanceRowQuantityControls(row) {
        const qtyInput = row.querySelector('.nm-easywholesale-qty');
        const decreaseBtn = row.querySelector('.nm-easywholesale-qty-decrease');
        const increaseBtn = row.querySelector('.nm-easywholesale-qty-increase');
        const rail = row.querySelector('.gc-row-rail') || row.querySelector('.gc-wholesale-rail');
        if (!qtyInput || !decreaseBtn || !increaseBtn || !rail || rail.dataset.gcQtyEnhanced === '1') return;

        const rowTitle = this.getRowDisplayName(row);
        if (rowTitle) {
            qtyInput.setAttribute('aria-label', `${rowTitle} quantity`);
            decreaseBtn.setAttribute('aria-label', `Decrease quantity for ${rowTitle}`);
            increaseBtn.setAttribute('aria-label', `Increase quantity for ${rowTitle}`);
        }

        if (!decreaseBtn.dataset.gcHoldBound) {
            this.bindLongPressAdjust(decreaseBtn);
            decreaseBtn.dataset.gcHoldBound = '1';
        }

        if (!increaseBtn.dataset.gcHoldBound) {
            this.bindLongPressAdjust(increaseBtn);
            increaseBtn.dataset.gcHoldBound = '1';
        }

        if (this.enableQtyQuickPresets) {
            this.injectQtyPresets(row, rail, qtyInput, rowTitle || 'item');
        } else {
            const existingPresets = rail.querySelector('.gc-qty-presets');
            if (existingPresets) existingPresets.remove();
        }

        rail.dataset.gcQtyEnhanced = '1';
    }

    bindLongPressAdjust(button) {
        if (!button || button.dataset.gcPressBound) return;
        button.dataset.gcPressBound = '1';
    }

    injectQtyPresets(row, rail, qtyInput, rowTitle) {
        if (rail.querySelector('.gc-qty-presets')) return;
        const presetContainer = document.createElement('span');
        presetContainer.className = 'gc-qty-presets';
        presetContainer.setAttribute('aria-label', `Quick quantity presets for ${rowTitle}`);

        [1, 5, 10].forEach((amount) => {
            const presetBtn = document.createElement('button');
            presetBtn.type = 'button';
            presetBtn.className = 'gc-qty-preset-btn';
            presetBtn.textContent = `+${amount}`;
            presetBtn.setAttribute('aria-label', `Increase ${rowTitle} by ${amount}`);

            presetBtn.addEventListener('click', () => {
                const current = parseInt(qtyInput.value, 10) || 0;
                const next = current + amount;
                qtyInput.value = String(next);
                qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
                qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
            });

            presetContainer.appendChild(presetBtn);
        });

        rail.appendChild(presetContainer);
    }

    /**
     * Inject Massy discounted prices into Easy Wholesale price elements
     * Uses Massy.getPrice() API to fetch real discounted prices dynamically
     */
    async injectMassyPriceContainers() {
        // Inject discounts into variant rows on product and quick-order pages

        // Check if Massy is available
        if (!window.Massy || !window.Massy.getPrice) {
            console.log('[WholesaleAddAll] Massy.getPrice not available, skipping price injection');
            return;
        }

        // Check if user has a tier (only show discounts for logged-in tier customers)
        if (!window.customerTier) {
            console.log('[WholesaleAddAll] No customer tier detected, skipping Massy prices');
            return;
        }

        // Find all Easy Wholesale price elements
        const priceElements = document.querySelectorAll('.nm-easywholesale-price');
        let processedCount = 0;

        for (const priceElement of priceElements) {
            // Skip if already processed
            if (priceElement.dataset.massyInjected === 'true') {
                continue;
            }

            // Get product info
            const parentItem = priceElement.closest('.nm-easywholesale-item');
            if (!parentItem) {
                continue;
            }

            // Get variant ID - try multiple sources
            let variantId = parentItem.dataset.variantId ||
                parentItem.querySelector('[data-variant-id]')?.dataset.variantId ||
                parentItem.querySelector('[data-id]')?.dataset.id ||
                parentItem.querySelector('.nm-easywholesale-qty-increase')?.id?.replace('nm-', '');

            if (!variantId) {
                continue;
            }

            // Clean the variant ID (remove any prefixes)
            variantId = variantId.toString().replace(/^nm-/, '');

            // Get the current/original price text
            const currentPriceText = priceElement.textContent.trim();
            const priceMatch = currentPriceText.match(/\$([\d,]+\.?\d*)/);
            if (!priceMatch) {
                continue;
            }

            const originalPriceValue = parseFloat(priceMatch[1].replace(',', ''));
            const originalPriceFormatted = '$' + originalPriceValue.toFixed(2);

            try {
                // Call Massy API to get discounted price
                const discountedPrice = await window.Massy.getPrice(variantId);

                // Skip if no discount or same as original
                if (!discountedPrice || discountedPrice <= 0 || discountedPrice >= originalPriceValue) {
                    // Mark as processed even if no discount
                    priceElement.dataset.massyInjected = 'true';
                    continue;
                }

                const discountedPriceFormatted = '$' + discountedPrice.toFixed(2);

                // Create the Massy wrapper structure
                const wrapper = document.createElement('span');
                wrapper.className = 'massy-custom-pricing-price-wrapper';

                // Original price (crossed out)
                const originalSpan = document.createElement('span');
                originalSpan.className = 'massy-custom-pricing-price-original';
                originalSpan.textContent = originalPriceFormatted;

                // Discounted price with tier class for badge coloring
                const discountSpan = document.createElement('span');
                discountSpan.className = 'massy-custom-pricing-price-discount';
                if (window.customerTier) {
                    discountSpan.classList.add(window.customerTier + '-tier');
                }
                discountSpan.textContent = discountedPriceFormatted;

                wrapper.appendChild(originalSpan);
                wrapper.appendChild(discountSpan);

                // Replace the price element content
                priceElement.innerHTML = '';
                priceElement.appendChild(wrapper);

                // Store the discounted price for tooltip use
                priceElement.dataset.massyDiscountedPrice = discountedPrice;
                priceElement.dataset.massyOriginalPrice = originalPriceValue;

                processedCount++;
            } catch (error) {
                console.warn('[WholesaleAddAll] Error getting Massy price for variant', variantId, error);
            }

            // Mark as processed
            priceElement.dataset.massyInjected = 'true';
        }

        this.rebuildWholesaleRows();
        this.enhanceQuantityInteractions();
        this.applyNameAccessibilityTitles();
        this.updateResponsiveRowModes();
        console.log(`[WholesaleAddAll] Injected Massy discounts into ${processedCount}/${priceElements.length} price elements`);

        // After injecting prices, setup the total observer
        this.observeAndUpdateTotal();
    }

    /**
     * Create sticky footer bar for product pages - uses exact same structure as Easy Wholesale app
     */
    setupStickyFooter() {
        const isProductPage = window.location.pathname.includes('/products/');
        if (!isProductPage) return;

        // Find the existing sticky created by Easy Wholesale app (it's hidden on product pages)
        const existingSticky = document.querySelector('.nm-wholesale-main-fix');

        if (existingSticky) {
            // App creates this but hides it on product pages - force it visible
            // IMPORTANT: Only set visibility properties, NOT positioning
            // The app's CSS already has proper responsive positioning via media queries
            // Setting inline positioning would override those media queries
            console.log('[WholesaleAddAll] Found hidden app sticky, making it visible');

            existingSticky.style.setProperty('display', 'block', 'important');
            existingSticky.style.setProperty('visibility', 'visible', 'important');
            existingSticky.style.setProperty('opacity', '1', 'important');

            // Also show any hidden child elements
            const innerFix = existingSticky.querySelector('.nm-wholesale-fix');
            if (innerFix) {
                innerFix.style.setProperty('display', 'block', 'important');
                innerFix.style.setProperty('visibility', 'visible', 'important');
            }

            // Hide the inline footer since we're using the sticky
            const inlineFooter = document.querySelector('.nm-easywholesale-footer:not(.nm-wholesale-main-fix .nm-easywholesale-footer)');
            if (inlineFooter) inlineFooter.style.display = 'none';

            // Hide add to cart button - try multiple selectors for mobile compatibility
            const addToCartSelectors = [
                '.nm-easywholesale-add-to-cart-container:not(.nm-wholesale-main-fix .nm-easywholesale-add-to-cart-container)',
                '.nm-wholesale-btn-add-to-cart:not(.nm-wholesale-main-fix .nm-wholesale-btn-add-to-cart)',
                'button.nm-wholesale-btn-add-to-cart',
                '.nm-easywholesale-footer button:not(.nm-wholesale-main-fix button)',
                '.nm-wholesale-table button[name="add"]:not(.nm-wholesale-main-fix button)'
            ];

            addToCartSelectors.forEach(selector => {
                const element = document.querySelector(selector);
                if (element) {
                    console.log(`[WholesaleAddAll] Hiding add to cart with selector: ${selector}`);
                    element.style.display = 'none';
                }
            });

            // Price updates are now handled by setupAllStickyPrices() which runs on ALL pages
            return;
        }

        // Fallback: If no app sticky exists, wait for wholesale content
        const existingFooter = document.querySelector('.nm-easywholesale-footer');
        if (!existingFooter) return;

        console.log('[WholesaleAddAll] No app sticky found, creating our own');

        // Hide inline footer
        existingFooter.style.display = 'none';

        // Hide add to cart button - try multiple selectors for mobile compatibility
        const addToCartSelectors = [
            '.nm-easywholesale-add-to-cart-container',
            '.nm-wholesale-btn-add-to-cart',
            'button.nm-wholesale-btn-add-to-cart',
            '.nm-easywholesale-footer button',
            '.nm-wholesale-table button[name="add"]'
        ];

        addToCartSelectors.forEach(selector => {
            const element = document.querySelector(selector);
            if (element) {
                console.log(`[WholesaleAddAll] Fallback: Hiding add to cart with selector: ${selector}`);
                element.style.display = 'none';
            }
        });

        // Create sticky with app's structure
        const stickyFooter = document.createElement('div');
        stickyFooter.className = 'nm-wholesale-main-fix';
        stickyFooter.innerHTML = `
            <div class="nm-wholesale-fix">
                <div class="nm-wholesale-fix-inner">
                    <div class="nm-easywholesale-footer">
                        <div class="nm-easywholesale-selected nm-primary-color">Total selected items: <span class="nm-total-selected-items-fix">0</span></div>
                        <div class="nm-easywholesale-total-fix nm-primary-color">$0.00</div>
                    </div>
                    <div class="nm-easywholesale-add-to-cart-container-page-fix">
                        <button type="button" class="nm-wholesale-btn-add-to-cart nm-secondary-background-color">ADD TO CART</button>
                    </div>
                </div>
            </div>
        `;
        document.body.appendChild(stickyFooter);
        // Price updates handled by setupAllStickyPrices() which runs after this
    }

    /**
     * Setup Massy price updates for sticky footer
     */
    setupStickyPriceUpdates(stickyFooter) {
        const stickyTotal = stickyFooter.querySelector('.nm-easywholesale-total-fix');
        if (!stickyTotal) return;

        let isUpdating = false; // Guard against infinite loops

        const updateStickyTotal = () => {
            if (isUpdating) return;

            let totalPrice = 0;

            document.querySelectorAll('.nm-easywholesale-item').forEach(item => {
                const qtyInput = item.querySelector('.nm-easywholesale-qty');
                const qty = qtyInput ? parseInt(qtyInput.value) || 0 : 0;
                if (qty <= 0) return;

                const priceEl = item.querySelector('.nm-easywholesale-price');
                if (priceEl?.dataset.massyDiscountedPrice) {
                    totalPrice += qty * parseFloat(priceEl.dataset.massyDiscountedPrice);
                }
            });

            if (totalPrice > 0) {
                const formatted = '$' + totalPrice.toFixed(2);
                if (stickyTotal.textContent.trim() !== formatted) {
                    isUpdating = true;
                    stickyTotal.textContent = formatted;
                    setTimeout(() => { isUpdating = false; }, 100);
                }
            }
        };

        // Observe the sticky total for app updates and override with Massy prices
        new MutationObserver(() => {
            if (!isUpdating) requestAnimationFrame(updateStickyTotal);
        }).observe(stickyTotal, {
            childList: true, characterData: true, subtree: true
        });

        // Also update on quantity changes
        document.querySelectorAll('.nm-easywholesale-qty').forEach(input => {
            input.addEventListener('change', () => setTimeout(updateStickyTotal, 50));
            input.addEventListener('input', () => setTimeout(updateStickyTotal, 50));
        });

        updateStickyTotal();
    }

    /**
     * Observe and update the wholesale total (#nm-total-summ-selected) with Massy discounted prices
     */
    observeAndUpdateTotal() {
        const totalElement = document.querySelector('#nm-total-summ-selected');
        if (!totalElement) return;

        let isUpdating = false;

        const updateTotal = () => {
            if (isUpdating) return;

            let discountedTotal = 0;
            let hasDiscounts = false;

            document.querySelectorAll('.nm-easywholesale-item').forEach(item => {
                const qtyInput = item.querySelector('.nm-easywholesale-qty');
                const qty = qtyInput ? parseInt(qtyInput.value) || 0 : 0;
                if (qty <= 0) return;

                const priceEl = item.querySelector('.nm-easywholesale-price');
                if (priceEl?.dataset.massyDiscountedPrice) {
                    discountedTotal += qty * parseFloat(priceEl.dataset.massyDiscountedPrice);
                    hasDiscounts = true;
                }
            });

            if (hasDiscounts && discountedTotal > 0) {
                const formatted = '$' + discountedTotal.toFixed(2);
                if (totalElement.textContent.trim() !== formatted) {
                    isUpdating = true;
                    totalElement.textContent = formatted;
                    console.log('[WholesaleAddAll] Updated total to Massy discounted price:', formatted);
                    setTimeout(() => { isUpdating = false; }, 100);
                }
            }
        };

        // Run immediately and observe total element for wholesale app overwrites
        updateTotal();

        new MutationObserver(() => requestAnimationFrame(updateTotal)).observe(totalElement, {
            childList: true, characterData: true, subtree: true
        });
    }

    observeWholesaleChanges() {
        // Watch for dynamically loaded wholesale content
        const observer = new MutationObserver((mutations) => {
            let shouldSetup = false;

            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === 1 && (
                        node.classList?.contains('nm-easywholesale-product') ||
                        node.querySelector?.('.nm-easywholesale-product')
                    )) {
                        shouldSetup = true;
                    }
                });
            });

            if (shouldSetup) {
                setTimeout(() => {
                    this.setupButtons();
                    this.injectMassyPriceContainers();
                    this.rebuildWholesaleRows();
                    this.applyNameAccessibilityTitles();
                    this.setupStickyFooter();
                    this.setupAllStickyPrices();
                    this.enhanceQuantityInteractions();
                    this.updateResponsiveRowModes();
                    this.scheduleCartQtyRefresh(0);
                }, 100);
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }
}

// Singleton instance to prevent duplicate button handlers
let wholesaleAddAllInstance = null;

function initWholesaleAddAll() {
    if (!document.querySelector('.nm-easywholesale-container, .nm-wholesale-container')) {
        return;
    }

    if (wholesaleAddAllInstance) {
        // Instance exists - retry Massy price injection and sticky prices
        console.log('[WholesaleAddAll] Already initialized, retrying Massy price injection and sticky prices');
        wholesaleAddAllInstance.injectMassyPriceContainers();
        wholesaleAddAllInstance.rebuildWholesaleRows();
        wholesaleAddAllInstance.setupStickyFooter();
        wholesaleAddAllInstance.setupAllStickyPrices();
        wholesaleAddAllInstance.scheduleCartQtyRefresh(0);
        return;
    }

    console.log('[WholesaleAddAll] Initializing singleton instance');
    wholesaleAddAllInstance = new WholesaleAddAll();
}

// Initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', initWholesaleAddAll);

// Retry later to catch Massy API and wholesale content that loads after DOM
setTimeout(initWholesaleAddAll, 1000);
setTimeout(initWholesaleAddAll, 2000);

// Dedicated sticky footer check for product pages - keeps checking until wholesale content loads
if (window.location.pathname.includes('/products/')) {
    let stickyCheckCount = 0;
    const stickyCheckInterval = setInterval(() => {
        stickyCheckCount++;
        const footer = document.querySelector('.nm-easywholesale-footer');
        const stickyExists = document.querySelector('.nm-wholesale-main-fix');

        if (footer && !stickyExists && wholesaleAddAllInstance) {
            console.log('[WholesaleAddAll] Sticky check found footer, creating sticky');
            wholesaleAddAllInstance.setupStickyFooter();
        }

        // Stop checking after 10 seconds or if sticky is created
        if (stickyCheckCount > 20 || stickyExists) {
            clearInterval(stickyCheckInterval);
        }
    }, 500);
}
